import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Heart } from "lucide-react";
import { PROGRAMS } from "../data/site";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  {
    label: "What We Do",
    to: "/what-we-do",
    children: PROGRAMS.slice(0, 6).map((p) => ({ label: p.title, to: `/what-we-do#${p.slug}` })),
  },
  {
    label: "Get Involved",
    to: "/get-involved",
    children: [
      { label: "Donate", to: "/get-involved#donate" },
      { label: "Partner With Us", to: "/get-involved#partner" },
      { label: "Fundraise", to: "/get-involved#fundraise" },
    ],
  },
  { label: "News", to: "/news" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-brand-cream/85 backdrop-blur-xl shadow-[0_1px_0_rgba(216,207,192,0.9)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-brand-ink/55 to-transparent transition-opacity duration-500 ${
            scrolled ? "opacity-0" : "opacity-100"
          }`}
        />
        <div className="relative flex h-[76px] items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-primary text-brand-cream">
              <Heart className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <span className="leading-none">
              <span className={`block font-serif text-lg tracking-tight transition-colors duration-300 ${scrolled ? "text-brand-ink" : "text-brand-cream"}`}>Equipping New Life</span>
              <span className={`block text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${scrolled ? "text-brand-muted" : "text-brand-cream/70"}`}>Bishnupur · Manipur</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((item) => (
              <div key={item.label} className="relative group py-6">
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 text-sm font-medium link-underline transition-colors ${
                      isActive
                        ? "text-brand-accent"
                        : scrolled
                        ? "text-brand-ink/80 hover:text-brand-primary"
                        : "text-brand-cream/90 hover:text-brand-accent"
                    }`
                  }
                >
                  {item.label}
                  {item.children && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
                </NavLink>
                {item.children && (
                  <div className="invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300 absolute left-0 top-full min-w-[240px] rounded-2xl border border-brand-line bg-brand-cream/95 backdrop-blur-xl p-2 shadow-xl">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        className="block rounded-xl px-4 py-2.5 text-sm text-brand-ink/80 hover:bg-brand-surface hover:text-brand-primary transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/get-involved#donate"
              data-testid="donate-button-header"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-brand-cream hover:bg-brand-primaryHover transition-colors"
            >
              <Heart className="h-4 w-4" /> Donate
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden grid place-items-center h-11 w-11 rounded-full border transition-colors ${scrolled ? "border-brand-line text-brand-ink" : "border-brand-cream/40 text-brand-cream"}`}
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-brand-cream/97 backdrop-blur-xl border-t border-brand-line ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <div className="px-5 py-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block rounded-xl px-4 py-3 text-base font-medium text-brand-ink hover:bg-brand-surface"
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/get-involved#donate"
            data-testid="donate-button-mobile"
            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-brand-primary px-5 py-3 text-base font-semibold text-brand-cream"
          >
            <Heart className="h-4 w-4" /> Donate
          </Link>
        </div>
      </div>
    </header>
  );
}
